/**
 * Colours of an own 3D model in three modes (tri-server docs/building-models.md, "Farben"):
 *
 *  - original: the file's colours;
 *  - natural:  plausible real materials — from the IFC material (outermost layer) where the file
 *              names one, else from the part class;
 *  - house:    our LoD2 style per part class, so the model sits in the scene like its neighbours.
 *
 * The server writes `partClass`, `material` and `original` into each glTF material's extras; the
 * loader hands them over as `material.userData`. A GLB without them keeps its own colours in every
 * mode (`hasPartClasses` tells the controls). No wooden-model mode — on purpose.
 */

export type ColourMode = "original" | "natural" | "house";
export type PartClass = "wall" | "roof" | "slab" | "window" | "door" | "structure" | "railing" | "stair" | "furniture" | "other";

export interface PartInfo {
  partClass?: PartClass;
  material?: string;
  /** sRGB, as the file gave it. */
  original?: [number, number, number, number];
}

export interface PartAppearance {
  /** sRGB hex; null keeps the original colour. */
  colour: number | null;
  opacity: number;
  visible: boolean;
}

const GLASS = { colour: 0x9fb2c0, opacity: 0.45 };

/** Our LoD2 palette (Building3DView SURFACE_COLOURS) carried over to the parts. */
export const HOUSE_COLOURS: Record<PartClass, number> = {
  wall: 0xd2cbbf,
  roof: 0x9a5a48,
  slab: 0xc9c3b8,
  window: GLASS.colour,
  door: 0x7b6a5c,
  structure: 0x8a8580,
  railing: 0x6b6f74,
  stair: 0xc9c3b8,
  furniture: 0xc9c3b8,
  other: 0xc2bcb2,
};

/** Natural fallback when the file names no material: what such a part usually is. */
const NATURAL_BY_CLASS: Record<PartClass, number> = {
  wall: 0xe4ded1, // plaster
  roof: 0x55514c, // dark roofing
  slab: 0xb4b0a8, // concrete
  window: GLASS.colour,
  door: 0x6b4f3a, // wood
  structure: 0xa8a49d, // concrete
  railing: 0x4b4f54, // steel
  stair: 0xb4b0a8,
  furniture: 0xb4b0a8,
  other: 0xbdb7ad,
};

/**
 * Material keywords → colour, German and English, as material names come from Revit, ArchiCAD and
 * Allplan ("Masonry - Brick", "Mauerwerk Ziegel", "Beton C30/37", "Gipskarton"). First match wins,
 * so the more specific entries come first.
 */
const MATERIAL_COLOURS: Array<[RegExp, number]> = [
  [/gras|grass|gründach|green roof|substrat|vegetat/i, 0x6f8f4e],
  [/glas|glass|verglas/i, GLASS.colour],
  [/dachziegel|roof ?tile|dachstein|pfanne/i, 0x8c4a3a],
  [/klinker|ziegel|brick|backstein/i, 0x9b5642],
  [/schiefer|slate/i, 0x4a4d52],
  [/bitumen|epdm|abdichtung|membrane|roofing|dachbahn/i, 0x4a4a4a],
  [/kupfer|copper/i, 0x5f8f7f],
  [/zink|zinc/i, 0x8d9296],
  [/alu|alumin|stahl|steel|metal|metall|blech/i, 0x8c9196],
  [/holz|wood|timber|lumber|plywood|sperrholz|lärche|larch|eiche|oak/i, 0x9a7350],
  [/putz|plaster|stucco|render|gips|gypsum|plasterboard|drywall/i, 0xe6e0d4],
  [/naturstein|sandstein|granit|marmor|stone|limestone/i, 0xb3aa98],
  [/fliese|tile|keramik|ceramic/i, 0xb7b0a6],
  [/beton|concrete|zement|cement|block|kalksand|porenbeton/i, 0xa9a59e],
  [/dämm|insulation|luft|air/i, -1], // inner layers: skip, look at the next one
];

/** Pure: colour of the outermost layer whose material we recognise; null when none. */
export const naturalColourOfMaterial = (material?: string): number | null => {
  if (!material) return null;
  for (const layer of material.split(" / ")) {
    for (const [pattern, colour] of MATERIAL_COLOURS) {
      if (!pattern.test(layer)) continue;
      if (colour >= 0) return colour;
      break; // an inner layer: next layer
    }
  }
  return null;
};

/** Pure: is this part glass? The file marks it by transparency far more reliably than by class. */
const isGlass = (part: PartInfo) => (part.original?.[3] ?? 1) < 0.9;

/** Pure: how a part looks in a mode. */
export const appearanceFor = (mode: ColourMode, part: PartInfo): PartAppearance => {
  const original = { colour: null, opacity: part.original?.[3] ?? 1, visible: true };
  if (mode === "original" || !part.partClass) return original;
  // Furniture and fittings are noise from outside and would show through the glass in our colours.
  if (part.partClass === "furniture") return { colour: null, opacity: 1, visible: false };
  if (isGlass(part)) return { colour: GLASS.colour, opacity: GLASS.opacity, visible: true };
  if (mode === "house") return { colour: HOUSE_COLOURS[part.partClass], opacity: 1, visible: true };
  return { colour: naturalColourOfMaterial(part.material) ?? NATURAL_BY_CLASS[part.partClass], opacity: 1, visible: true };
};

/** Does a loaded model carry part classes (IFC), so that the colour modes can do anything? */
export const hasPartClasses = (model: any): boolean => {
  let found = false;
  model?.traverse?.((child: any) => {
    const materials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
    if (materials.some((m: any) => m?.userData?.partClass)) found = true;
  });
  return found;
};

/**
 * Apply a mode to a loaded model. The first call remembers each material's own colour and opacity,
 * so switching back to "original" restores exactly what was loaded.
 */
export const applyAppearance = (model: any, mode: ColourMode, THREE: any): void => {
  model?.traverse?.((child: any) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    let anyVisible = false;
    for (const material of materials) {
      if (!material) continue;
      const data = material.userData ?? {};
      if (!data.__loaded) {
        data.__loaded = { colour: material.color?.clone?.(), opacity: material.opacity, transparent: material.transparent };
        material.userData = data;
      }
      const look = appearanceFor(mode, data as PartInfo);
      if (look.colour === null) {
        if (data.__loaded.colour) material.color.copy(data.__loaded.colour);
        material.opacity = data.__loaded.opacity;
        material.transparent = data.__loaded.transparent;
      } else {
        material.color.copy(new THREE.Color(look.colour).convertSRGBToLinear());
        material.opacity = look.opacity;
        material.transparent = look.opacity < 1;
      }
      material.needsUpdate = true;
      anyVisible = anyVisible || look.visible;
    }
    child.visible = anyVisible;
  });
};
